import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OptionSelect } from "@/components/data/FieldInput";
import { PageHeader } from "@/components/data/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtMoney, fmtNum, monthStart, today } from "@/lib/api";
import { exportExcel, exportPDF } from "@/lib/export";

const iso = (d) => d.toISOString().slice(0, 10);
const PRESETS = [
  ["Aujourd'hui", () => [today(), today()]],
  ["Cette semaine", () => { const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return [iso(d), today()]; }],
  ["Ce mois", () => [monthStart(), today()]],
  ["Cette année", () => [`${today().slice(0, 4)}-01-01`, today()]],
];

function ReportTable({ cols, rows, showPeriode }) {
  const fmt = (c, v) => (v == null ? "—" : c.money ? fmtMoney(v) : fmtNum(v));
  const tot = Object.fromEntries(cols.map((c) => [c.k, rows.reduce((s, r) => s + Number(r[c.k] || 0), 0)]));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="report-table">
        <thead className="bg-muted/60 text-[11px] uppercase tracking-wider text-muted-foreground">
          <tr>{showPeriode && <th className="px-3 py-2.5 text-left">Période</th>}<th className="px-3 py-2.5 text-left">Groupe</th>{cols.map((c) => <th key={c.k} className="px-3 py-2.5 text-right whitespace-nowrap">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.periode || ""}|${r.groupe}`} className="border-t hover:bg-accent/40" data-testid="report-row">
              {showPeriode && <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.periode}</td>}
              <td className="px-3 py-2">{r.groupe}</td>
              {cols.map((c) => <td key={c.k} className="px-3 py-2 text-right font-mono whitespace-nowrap">{fmt(c, r[c.k])}</td>)}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={cols.length + 2} className="py-14 text-center text-muted-foreground" data-testid="report-empty">Aucune donnée sur la période</td></tr>}
        </tbody>
        {rows.length > 0 && (
          <tfoot className="border-t-2 border-foreground/80 font-semibold bg-muted/40">
            <tr>{showPeriode && <td />}<td className="px-3 py-2">Total</td>{cols.map((c) => <td key={c.k} className="px-3 py-2 text-right font-mono whitespace-nowrap" data-testid={`report-total-${c.k}`}>{fmt(c, tot[c.k])}</td>)}</tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export default function RapportsPage() {
  const { meta, can } = useAuth();
  const [defs, setDefs] = useState(null);
  const [p, setP] = useState({ type: "tfj", group: "activite", periodicite: "periode", date_from: monthStart(), date_to: today() });
  const [data, setData] = useState({ rows: [], cols: [] });
  useEffect(() => {
    api.get("/rapports/types").then((r) => setDefs(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    api.get("/rapports", { params: p }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [p]);
  if (!defs) return null;
  const def = defs.types.find((t) => t.value === p.type);
  const showPeriode = data.periodicite && data.periodicite !== "periode";
  const perLabel = defs.periodicites.find((x) => x.value === p.periodicite)?.label;
  const dimLabel = def.dims.find((d) => d.value === p.group)?.label || "";
  const title = `Rapport ${def.label} — ${dimLabel}${showPeriode ? ` — ${perLabel}` : ""}`;
  const exportCols = [...(showPeriode ? [{ label: "Période", raw: (r) => r.periode }] : []), { label: "Groupe", raw: (r) => r.groupe },
    ...data.cols.map((c) => ({ label: c.label, raw: (r) => (r[c.k] == null ? "" : Number(r[c.k])) }))];
  const sub = `Période du ${p.date_from} au ${p.date_to}`;
  const setType = (v) => v && setP({ ...p, type: v, group: defs.types.find((t) => t.value === v).dims[0].value });
  return (
    <div>
      <PageHeader title="Rapports" subtitle="Rapports quotidiens, hebdomadaires et mensuels calculés en direct sur la base (TFJ importés, opérations validées)." />
      <div className="bg-card border rounded-lg">
        <div className="p-3 sm:p-4 border-b flex flex-wrap gap-2 items-center">
          <div className="w-full sm:w-56"><OptionSelect options={defs.types} value={p.type} onChange={setType} testId="report-type-select" /></div>
          <div className="w-full sm:w-48"><OptionSelect options={def.dims} value={p.group} onChange={(v) => v && setP({ ...p, group: v })} testId="report-group-select" /></div>
          {p.type !== "tresorerie" && <div className="w-full sm:w-44"><OptionSelect options={defs.periodicites} value={p.periodicite} onChange={(v) => v && setP({ ...p, periodicite: v })} testId="report-periodicite-select" /></div>}
          <Input type="date" className="h-9 w-[150px]" value={p.date_from} onChange={(e) => setP({ ...p, date_from: e.target.value })} data-testid="report-date-from" />
          <Input type="date" className="h-9 w-[150px]" value={p.date_to} onChange={(e) => setP({ ...p, date_to: e.target.value })} data-testid="report-date-to" />
          {can("rapports", "exporter") && (
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={() => exportExcel(title, exportCols, data.rows)} data-testid="report-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
              <Button variant="outline" size="sm" onClick={() => exportPDF(title, exportCols, data.rows, { company: meta.settings?.nom_entreprise, subtitle: sub, footer: meta.settings?.pied_de_page_rapports })} data-testid="report-export-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
            </div>
          )}
        </div>
        <div className="px-3 sm:px-4 py-2 border-b flex flex-wrap gap-1.5">
          {PRESETS.map(([l, fn], i) => (
            <Button key={l} variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { const [a, b] = fn(); setP({ ...p, date_from: a, date_to: b }); }} data-testid={`report-preset-${i}`}>{l}</Button>
          ))}
        </div>
        <ReportTable cols={data.cols} rows={data.rows} showPeriode={showPeriode} />
      </div>
    </div>
  );
}
